import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth } from '../utils/api';
import { formatCurrency } from '../utils/AuthUtils';
import { Users, Calendar, Receipt, TrendingUp, Clock, AlertCircle } from 'lucide-react';

const Dashboard = ({ currentBranch }) => {
  const { print_lang } = useLanguage();
  const [stats, setStats] = useState(null);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [statsData, appointmentsData] = await Promise.all([
        fetchWithAuth(`/api/dashboard/stats?branch_id=${currentBranch.branch_id}`),
        fetchWithAuth(`/api/dashboard/upcoming-appointments?branch_id=${currentBranch.branch_id}&limit=5`)
      ]);

      if (statsData.success) {
        setStats(statsData.stats);
      }
      if (appointmentsData.success) {
        setUpcomingAppointments(appointmentsData.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentBranch]);

  useEffect(() => {
    if (currentBranch?.branch_id) {
      fetchDashboardData();
    }
  }, [currentBranch, fetchDashboardData]);

  const statCards = [
    {
      title: print_lang('patients', 'Patients'),
      value: stats?.totalPatients || 0,
      icon: Users,
      color: 'blue',
      subtitle: `+${stats?.newPatientsThisMonth || 0} ${print_lang('this_month', 'this month')}`
    },
    {
      title: print_lang('today', 'Today') + ' ' + print_lang('appointments', 'Appointments'),
      value: stats?.todayAppointments || 0,
      icon: Calendar,
      color: 'green',
      subtitle: print_lang('scheduled', 'Scheduled')
    },
    {
      title: print_lang('today', 'Today') + ' ' + print_lang('revenue', 'Revenue'),
      value: formatCurrency(stats?.todayRevenue || 0),
      icon: Receipt,
      color: 'purple',
      subtitle: print_lang('paid', 'Paid')
    },
    {
      title: print_lang('this_month', 'This Month') + ' ' + print_lang('revenue', 'Revenue'),
      value: formatCurrency(stats?.monthRevenue || 0),
      icon: TrendingUp,
      color: 'orange',
      subtitle: print_lang('total', 'Total')
    }
  ];

  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500'
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        {print_lang('dashboard', 'Dashboard')}
      </h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="card">
              <div className="flex items-center justify-between mb-4">
                <div className={`${colorClasses[stat.color]} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-gray-500 text-sm">{stat.title}</h3>
              <p className="text-2xl font-bold text-gray-800 mt-1">{stat.value}</p>
              <p className="text-sm text-gray-400 mt-1">{stat.subtitle}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Appointments */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Clock size={20} />
            {print_lang('appointments', 'Appointments')} {print_lang('today', 'Today')}
          </h2>

          {upcomingAppointments.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              {print_lang('no_appointments', 'No appointments')}
            </p>
          ) : (
            <div className="space-y-3">
              {upcomingAppointments.map((apt) => (
                <div
                  key={apt.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-800">{apt.patient_name}</p>
                    <p className="text-sm text-gray-500">
                      {apt.type_name} - {apt.dentist_name}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-blue-600">
                      {new Date(apt.start_date).toLocaleTimeString('th-TH', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                    <span className={`badge badge-${apt.status === 'scheduled' ? 'info' : 'success'}`}>
                      {apt.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pending Invoices */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <AlertCircle size={20} />
            {print_lang('pending', 'Pending')} {print_lang('invoices', 'Invoices')}
          </h2>

          <div className="text-center py-8">
            <p className="text-3xl font-bold text-orange-600">
              {stats?.pendingInvoicesCount || 0}
            </p>
            <p className="text-gray-500 mt-2">
              {print_lang('total', 'Total')}: {formatCurrency(stats?.pendingInvoicesAmount || 0)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
